import { BuildOpts, Period, SlotCell, SlotStatus } from '../../../types/Exams';
import {
  addMinutes,
  localDateTime,
  makeLocalSlotStarts,
  minutesToHHMM,
  toLocalParts,
} from './slotHelpers';

export function buildSlotCellsFromPeriods(periods: Period[], opts: BuildOpts = {}): SlotCell[] {
  const {
    workStart = '09:30',
    workEnd = '17:30',
    stepMin = 30,
    timeZone = 'Europe/Kyiv',
    days,
  } = opts;

  // 1) Collect local days covered by periods (if not provided)
  const localDays = new Set<string>();
  for (const p of periods) {
    const s = new Date(p.start);
    const e = new Date(p.end);
    const startLocal = toLocalParts(s, timeZone).date;
    const endLocal = toLocalParts(e, timeZone).date;
    localDays.add(startLocal);
    localDays.add(endLocal);
    // (if you need all in-between days for multi-day spans, you could iterate here)
  }
  const dateList = (days && days.length ? days : Array.from(localDays)).sort();

  // 2) Precompute slot starts for a day (local minutes)
  const slotStarts = makeLocalSlotStarts(workStart, workEnd, stepMin);

  // 3) Build a fast list of normalized periods with local day buckets and UTC bounds
  type Norm = {
    id: string;
    startUTC: number;
    endUTC: number;
    status: SlotStatus;
    author: string;
    day: string;
  };
  const norm: Norm[] = [];
  for (const p of periods) {
    const sUTC = new Date(p.start).getTime();
    const eUTC = new Date(p.end).getTime();
    const dayLocal = toLocalParts(new Date(p.start), timeZone).date; // bucket by start day (good for <= daily spans)
    norm.push({
      id: p.id,
      startUTC: sUTC,
      endUTC: eUTC,
      status: p.author === 'student' ? 'taken' : 'closed',
      author: p.author,
      day: dayLocal,
    });
  }

  // 4) Create cells; mark overlap; priority: taken > closed > available
  const result: SlotCell[] = [];
  for (const day of dateList) {
    for (const startMin of slotStarts) {
      const slotStartLocal = localDateTime(day, minutesToHHMM(startMin), timeZone);
      const slotEndLocal = addMinutes(slotStartLocal, stepMin);
      const slotStartUTC = slotStartLocal.getTime();
      const slotEndUTC = slotEndLocal.getTime();

      let status: SlotStatus = 'available';
      let by: string | undefined;
      let id: string | undefined;
      for (const p of norm) {
        // Only check same day bucket quickly; remove this if your periods cross midnight often
        if (p.day !== day) continue;

        const overlap = slotStartUTC < p.endUTC && slotEndUTC > p.startUTC;
        if (overlap) {
          if (p.status === 'taken') {
            status = 'taken';
            by = p.author;
            id = p.id;
            break; // taken beats everything
          } else if (status === 'available' || status === 'closed') {
            status = 'closed';
            by = p.author;
            id = p.id;
            // don't break; keep scanning for 'taken'
          }
        }
      }

      result.push({
        id,
        date: day,
        time: minutesToHHMM(startMin),
        status,
        by,
      });
    }
  }

  return result;
}
